import { useState, useCallback, useContext } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import {
    ChatMessage,
    ChatState,
    AnalyticsResponse,
    AnalyticsChatRequest
} from '../../types/analyticsChat';

interface UseAnalyticsChatReturn {
    messages: ChatMessage[];
    isLoading: boolean;
    error: string | null;
    sendMessage: (query: string) => Promise<void>;
    clearMessages: () => void;
    retryLastMessage: () => Promise<void>;
}

const sendAnalyticsQuery = async (
    sessionId: string,
    request: AnalyticsChatRequest
): Promise<AnalyticsResponse> => {
    const { data } = await axios.post(
        `${API_URL}/analytics/ask`,
        request,
        { headers: { Authorization: sessionId } }
    );
    return data;
};

export const useAnalyticsChat = (): UseAnalyticsChatReturn => {
    const sessionContext = useContext(SessionContext);
    
    const [state, setState] = useState<ChatState>({
        messages: [],
        isLoading: false,
        error: null
    });

    // Build conversation history for context (last 4 messages = 2 exchanges)
    const buildConversationHistory = useCallback((messages: ChatMessage[]) => {
        return messages
            .slice(-4)
            .map(msg => ({
                role: msg.role,
                content: msg.role === 'user' 
                    ? msg.content 
                    : msg.response?.interpretation || msg.content
            }));
    }, []);

    // Send a message and get AI response
    const sendMessage = useCallback(async (query: string) => {
        if (!query.trim() || !sessionContext?.sessionId) return;

        const userMessage: ChatMessage = {
            id: uuidv4(),
            role: 'user',
            content: query,
            timestamp: new Date()
        };

        // Add user message and set loading
        setState(prev => ({
            ...prev,
            messages: [...prev.messages, userMessage],
            isLoading: true,
            error: null
        }));

        try {
            const conversationHistory = buildConversationHistory(state.messages);
            
            const response = await sendAnalyticsQuery(sessionContext.sessionId, {
                userQuery: query,
                conversationHistory
            });

            const assistantMessage: ChatMessage = {
                id: uuidv4(),
                role: 'assistant',
                content: response.interpretation,
                timestamp: new Date(),
                response
            };

            setState(prev => ({
                ...prev,
                messages: [...prev.messages, assistantMessage],
                isLoading: false
            }));

        } catch (error) {
            console.error('Analytics chat error:', error);
            
            const errorMessage = axios.isAxiosError(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Sorry, I encountered an error processing your request. Please try again.';

            const assistantMessage: ChatMessage = {
                id: uuidv4(),
                role: 'assistant',
                content: errorMessage,
                timestamp: new Date(),
                error: errorMessage
            };

            setState(prev => ({
                ...prev,
                messages: [...prev.messages, assistantMessage],
                isLoading: false,
                error: errorMessage
            }));
        }
    }, [sessionContext?.sessionId, state.messages, buildConversationHistory]);

    // Clear all messages
    const clearMessages = useCallback(() => {
        setState({
            messages: [],
            isLoading: false,
            error: null
        });
    }, []);

    // Retry the last user message
    const retryLastMessage = useCallback(async () => {
        const lastUserMessage = [...state.messages]
            .reverse()
            .find(msg => msg.role === 'user');

        if (lastUserMessage) {
            // Remove the last assistant message (error response)
            setState(prev => ({
                ...prev,
                messages: prev.messages.slice(0, -1)
            }));
            
            // Resend the query
            await sendMessage(lastUserMessage.content);
        }
    }, [state.messages, sendMessage]);

    return {
        messages: state.messages,
        isLoading: state.isLoading,
        error: state.error,
        sendMessage,
        clearMessages,
        retryLastMessage
    };
};

export default useAnalyticsChat;

